import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { getOdcHeaderData, getOdcOrderById, updateOdcOrderCart } from '../utilities/api';
import OdcEditHeader from './OdcEditHeader';
import OdcEditContent from './OdcEditContent';

const OdcEditPage = ({ role, billIdFromProps, isDrawer, onSaveSuccess }) => {
    const { id: paramId } = useParams();
    const id = billIdFromProps || paramId;
    const navigate = useNavigate();
    const toast = useRef(null);
    const companyInitialized = useRef(false);

    const [allData, setAllData] = useState({ companies: [], categories: [], products: [], settings: [] });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [remarks, setRemarks] = useState('');
    const [orderEditedBy, setOrderEditedBy] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerGst, setCustomerGst] = useState('');
    const [orderDate, setOrderDate] = useState(null);
    const [orderTime, setOrderTime] = useState(null);
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [deliveryTime, setDeliveryTime] = useState(null);
    const [sessionInfo, setSessionInfo] = useState({ ubranch: null, userid: null, branchid: null });
    const [loading, setLoading] = useState(false);

    // Raw values from order header used to resolve dropdowns after allData loads
    const [orderCompanyId, setOrderCompanyId] = useState(null);
    const [orderMop, setOrderMop] = useState(null);

    // Single fetch on mount — all reference data
    useEffect(() => {
        const bid = localStorage.getItem('branchid');
        setSessionInfo({ ubranch: localStorage.getItem('branch'), userid: localStorage.getItem('userid'), branchid: bid });

        const fetchHeader = async () => {
            try {
                const result = await getOdcHeaderData({ branch_id: bid });
                setAllData({
                    companies: result.companies || [],
                    categories: result.categories || [],
                    products: result.products || [],
                    settings: result.settings || []
                });
            } catch (err) {
                console.error('Error fetching header data:', err);
            }
        };
        fetchHeader();
    }, []);

    // Load existing order
    useEffect(() => {
        if (!id) return;
        const fetchOrder = async () => {
            try {
                const res = await getOdcOrderById(id);
                const { header, items } = res;
                setRemarks(header.remarks || '');
                setOrderEditedBy(header.order_edited_by || '');
                setCustomerName(header.customer_name || '');
                setCustomerPhone(header.customer_phone || '');
                setCustomerGst(header.customer_gst || '');
                setOrderDate(header.bill_date ? new Date(header.bill_date) : null);
                setOrderTime(header.bill_time ? moment(header.bill_time, 'hh:mm A').toDate() : null);
                setDeliveryDate(header.delivery_date ? new Date(header.delivery_date) : null);
                setDeliveryTime(header.delivery_time ? moment(header.delivery_time, 'hh:mm A').toDate() : null);
                setOrderCompanyId(header.company_id ? String(header.company_id) : null);
                setOrderMop(header.mop || null);
                setCartItems(
                    items.map((i, index) => ({
                        uniqueId: `existing-${i.spl_id}-${index}`,
                        id: i.spl_id,
                        name: i.product_name,
                        quantity: parseFloat(i.quantity) || 0,
                        basic_rate: parseFloat(i.basic_rate),
                        tax: parseFloat(i.tax_percentage),
                        category_id: i.prcat_cat_id,
                        pr_id: i.pr_id,
                        isExisting: true,
                        billed_product_id: i.bill_pr_id
                    }))
                );
            } catch (err) {
                console.error('Error fetching order details:', err);
            }
        };
        fetchOrder();
    }, [id]);

    // Resolve company from order once companies list is loaded
    useEffect(() => {
        if (!orderCompanyId || !allData.companies.length) return;
        const match = allData.companies.find((c) => String(c.company_id) === orderCompanyId);
        if (match) {
            companyInitialized.current = false; // allow setting without triggering downstream reset
            setSelectedCompany(match);
        }
    }, [orderCompanyId, allData.companies]);

    // Resolve payment mode from order once settings list is loaded
    useEffect(() => {
        if (!orderMop || !allData.settings.length) return;
        const match = allData.settings.find((s) => s.name === orderMop);
        setSelectedPaymentMode(match || null);
    }, [orderMop, allData.settings]);

    // Reset filter dropdowns when company is manually changed (skip initial pre-population)
    useEffect(() => {
        if (!companyInitialized.current) {
            companyInitialized.current = true;
            return;
        }
        setSelectedCategory(null);
        setSelectedProduct(null);
        setSelectedPaymentMode(null);
    }, [selectedCompany]);

    // Reset product when category changes
    useEffect(() => {
        setSelectedProduct(null);
    }, [selectedCategory]);

    // Client-side chained filtering
    const filteredCategories = selectedCompany ? allData.categories.filter((c) => c.company_id === selectedCompany.company_id) : [];

    const filteredProducts = selectedCategory ? allData.products.filter((p) => p.prcat_cat_id === selectedCategory.code) : [];

    const addToCart = () => {
        if (!selectedProduct) return;
        const exists = cartItems.find((i) => i.pr_id === selectedProduct.pr_id && !i.isExisting);
        if (exists) {
            setCartItems(cartItems.map((i) => (i.uniqueId === exists.uniqueId ? { ...i, quantity: i.quantity + 1 } : i)));
        } else {
            setCartItems([
                ...cartItems,
                {
                    uniqueId: `new-${selectedProduct.pr_id}-${Date.now()}`,
                    id: selectedProduct.pr_id,
                    name: selectedProduct.product_name,
                    basic_rate: parseFloat(selectedProduct.basic_rate),
                    tax: parseFloat(selectedProduct.basic_tax_percent),
                    quantity: 1.0,
                    isExisting: false,
                    pr_id: selectedProduct.pr_id,
                    category_id: selectedProduct.prcat_cat_id
                }
            ]);
        }
        setSelectedProduct(null);
    };

    const totalSummary = cartItems.reduce(
        (acc, i) => {
            const sub = (i.quantity || 0) * i.basic_rate;
            return { subtotal: acc.subtotal + sub, tax: acc.tax + (sub * i.tax) / 100 };
        },
        { subtotal: 0, tax: 0 }
    );

    const saveOrder = async () => {
        setLoading(true);
        const payload = {
            order_id: id,
            branch_id: sessionInfo.branchid,
            user_id: sessionInfo.userid,
            company_id: selectedCompany ? selectedCompany.company_id : null,
            company_name: selectedCompany ? selectedCompany.name : null,
            is_credit: selectedCompany?.name === 'ODC' ? 1 : 0,
            mop: selectedPaymentMode ? selectedPaymentMode.name : 'CREDIT',
            final_mop: selectedPaymentMode ? selectedPaymentMode.name : 'CREDIT',
            order_edited_by: orderEditedBy,
            customer_name: customerName,
            customer_phone: customerPhone,
            customer_gst: customerGst,
            remarks,
            bill_date: orderDate ? moment(orderDate).format('YYYY-MM-DD') : null,
            bill_time: orderTime ? moment(orderTime).format('hh:mm A') : null,
            delivery_date: deliveryDate ? moment(deliveryDate).format('YYYY-MM-DD') : null,
            delivery_time: deliveryTime ? moment(deliveryTime).format('hh:mm A') : null,
            subtotal: totalSummary.subtotal,
            total_tax_amount: totalSummary.tax,
            grand_total: totalSummary.subtotal + totalSummary.tax,
            items: cartItems.map((item) => {
                const lineSubtotal = item.quantity * item.basic_rate;
                return {
                    product_id: item.pr_id,
                    product_name: item.name,
                    spl_id: item.id,
                    quantity: item.quantity,
                    basic_rate: item.basic_rate,
                    tax_percentage: item.tax,
                    taxableamount: lineSubtotal,
                    tax_amount: (lineSubtotal * item.tax) / 100,
                    category_id: item.category_id,
                    is_existing: item.isExisting,
                    billed_product_id: item.billed_product_id || null,
                    splitted_tax_pecetage: item.tax / 2,
                    splitted_gst: (lineSubtotal * item.tax) / 100 / 2
                };
            })
        };
        try {
            const response = await updateOdcOrderCart(payload);
            if (response.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Order Updated', detail: `Order #${id} saved successfully`, life: 3000 });
                if (isDrawer && onSaveSuccess) {
                    setTimeout(() => onSaveSuccess(), 1500);
                } else {
                    setTimeout(() => navigate('/odcorderlist'), 2000);
                }
            } else {
                throw new Error(response.message || 'Update failed');
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Save Error', detail: err.message || 'Server error', life: 4000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-3">
            <Toast ref={toast} />
            <ConfirmDialog />
            <OdcEditHeader id={id} sessionInfo={sessionInfo} isDrawer={isDrawer} onBack={() => navigate('/odcorderlist')} />
            <OdcEditContent
                allData={allData}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                filteredCategories={filteredCategories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                filteredProducts={filteredProducts}
                cartItems={cartItems}
                setCartItems={setCartItems}
                onAddToCart={addToCart}
                selectedPaymentMode={selectedPaymentMode}
                setSelectedPaymentMode={setSelectedPaymentMode}
                customerName={customerName}
                setCustomerName={setCustomerName}
                customerPhone={customerPhone}
                setCustomerPhone={setCustomerPhone}
                customerGst={customerGst}
                setCustomerGst={setCustomerGst}
                orderDate={orderDate}
                setOrderDate={setOrderDate}
                orderTime={orderTime}
                setOrderTime={setOrderTime}
                deliveryDate={deliveryDate}
                setDeliveryDate={setDeliveryDate}
                deliveryTime={deliveryTime}
                setDeliveryTime={setDeliveryTime}
                orderEditedBy={orderEditedBy}
                setOrderEditedBy={setOrderEditedBy}
                remarks={remarks}
                setRemarks={setRemarks}
                totalSummary={totalSummary}
                loading={loading}
                onSave={saveOrder}
            />
        </div>
    );
};

export default OdcEditPage;
